import { SpecialStripDetails } from "@/api/generated/BMWInterfaces"
import { Desktop, Mobile } from "@/lib/common/MediaQueries"
import colors from "@/lib/theme/colors"
import styled from "@emotion/styled"
import Image from "next/image"

// Full-bleed banner; inner content is capped and centred so the text aligns with
// the page content rather than the raw viewport edge.
const Root = styled.section`
  position: relative;
  width: 100%;
  height: auto;
  aspect-ratio: 360 / 307;
  min-height: fit-content;
  ${(props) => props.theme.breakpoints.up("md")} {
    aspect-ratio: 1280 / 333;
  }
`

// next/image `fill` positions against Root (the nearest positioned ancestor);
// the Desktop/Mobile boxes are static, so each image covers the whole banner.
const Media = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
`

const Content = styled.div`
  position: relative;
  z-index: 2;
  margin: 0 auto;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  text-align: center;
  padding: 40px 16px;
  ${(props) => props.theme.breakpoints.up("md")} {
    align-items: flex-start;
    justify-content: center;
    text-align: right;
    padding: 40px;
  }
`

const TextBox = styled.div<{ $hasSubtitle: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: ${colors.white};
  ${(props) => props.theme.breakpoints.up("md")} {
    height: 100%;
    margin-top: ${(props) =>
      props.$hasSubtitle ? "calc(118.5px - 40px)" : "calc(146.5px - 40px)"};
  }
`

// Rich text from the CMS; `p { margin: 0 }` neutralises the default paragraph
// spacing so multi-line titles keep the design's tight line rhythm.
const Title = styled.div`
  font-size: 24px;
  font-weight: 700;
  line-height: normal;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 32px;
    font-weight: 400;
    line-height: 40px;
  }
  p {
    margin: 0;
  }
`

const Subtitle = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: normal;
  max-width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 18px;
    max-width: 504px;
  }
  p {
    margin: 0;
  }
`

type Props = {
  details: SpecialStripDetails
}

export default function BenefitHero({ details }: Props): React.ReactElement | null {
  const { title, subTitle, imageDesktop, imageMobile } = details
  // Fall back to the other breakpoint's image when one is missing.
  const desktopImage = imageDesktop?.imageUrl ? imageDesktop : imageMobile
  const mobileImage = imageMobile?.imageUrl ? imageMobile : imageDesktop

  if (!desktopImage?.imageUrl && !mobileImage?.imageUrl) {
    return null
  }

  const hasText = !!(title || subTitle)

  return (
    <Root>
      <Media>
        <Desktop>
          {desktopImage?.imageUrl && (
            <Image
              src={desktopImage.imageUrl}
              alt={desktopImage.alt || ""}
              fill
              sizes="100vw"
              priority
              style={{ objectFit: "cover" }}
            />
          )}
        </Desktop>
        <Mobile>
          {mobileImage?.imageUrl && (
            <Image
              src={mobileImage.imageUrl}
              alt={mobileImage.alt || ""}
              fill
              sizes="100vw"
              priority
              style={{ objectFit: "cover" }}
            />
          )}
        </Mobile>
      </Media>
      {hasText && (
        <Content>
          <TextBox $hasSubtitle={!!subTitle}>
            {title && <Title dangerouslySetInnerHTML={{ __html: title }} />}
            {subTitle && <Subtitle dangerouslySetInnerHTML={{ __html: subTitle }} />}
          </TextBox>
        </Content>
      )}
    </Root>
  )
}
